import React, { useState } from "react";
import {
  CircularProgress,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { DeleteOutlined } from "@mui/icons-material";
import { Button, ButtonCategory } from "./button/Button";
import { useTranslation } from "react-i18next";

export interface IActionAndConfirmProps {
  onRemove: () => Promise<any>;
  errorMessage?: string;
  isConfirmDisabled?: boolean;
  loading: boolean;
  confirmTitle: string;
  confirmContent: string | React.ReactNode;
  label?: string;
  children?: React.ReactElement;
  dialogChildren?: React.ReactNode;
  iconOnly?: boolean;
  onOpenModal?: () => void;
  openDialog?: boolean;
  onRemoveCompleted?: () => void;
  onClose?: () => void;
}
export const ActionAndConfirm: React.FC<IActionAndConfirmProps> = ({
  isConfirmDisabled,
  onRemove,
  errorMessage,
  loading,
  children,
  dialogChildren,
  confirmTitle,
  confirmContent,
  label,
  iconOnly,
  onOpenModal,
  openDialog,
  onRemoveCompleted,
  onClose,
}) => {
  const { t: translateUi } = useTranslation("ui");
  const [open, setOpen] = useState(openDialog || false);
  const handleOpen = () => {
    setOpen(true);
    onOpenModal?.();
  };
  const handleRemove = () => {
    onRemove().then((res) => {
      if (!res?.error) {
        setOpen(false);
        onRemoveCompleted?.();
      }
    });
  };
  const handleOnClose = () => {
    setOpen(false);
    onClose?.();
  };
  return (
    <>
      {!openDialog && (
        <>
          {children ? (
            React.cloneElement(children, {
              onClick: () => {
                children.props.onClick?.();
                handleOpen();
              },
            })
          ) : (
            <IconButton
              size="small"
              onClick={() => {
                setOpen(true);
                onOpenModal?.();
              }}
              aria-label={label || translateUi("actions.remove")}
              title={label || translateUi("actions.remove")}
            >
              {loading ? (
                <CircularProgress
                  aria-label="loading"
                  size="16px"
                  color="inherit"
                />
              ) : (
                <DeleteOutlined
                  fontSize="small"
                  sx={{ color: iconOnly ? "error.main" : "inherit" }}
                />
              )}
            </IconButton>
          )}
        </>
      )}
      {errorMessage && (
        <Typography color="error" variant="caption">
          {errorMessage}
        </Typography>
      )}
      <Dialog
        open={open}
        onClose={handleOnClose}
        aria-labelledby="alert-dialog-confirmTitle"
        aria-describedby="alert-dialog-description"
        maxWidth={"sm"}
      >
        <DialogTitle
          sx={{ textAlign: "start", pb: 0.5 }}
          variant="h6"
          id="alert-dialog-confirmTitle"
        >
          {confirmTitle}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <DialogContentText
            sx={{
              textAlign: "start",
              color: "text.secondary",
              whiteSpace: "pre-line",
            }}
            id="alert-dialog-description"
            variant="body2"
          >
            {confirmContent}
          </DialogContentText>
          {dialogChildren}
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            mb: 2,
            pr: 3,
            gap: 1,
          }}
        >
          <Button onClick={handleOnClose} category={ButtonCategory.Secondary}>
            {translateUi("forms.cancel")}
          </Button>
          <Button
            category={ButtonCategory.Primary}
            data-cy="confirm"
            disabled={isConfirmDisabled || !!errorMessage || loading}
            endIcon={
              (isConfirmDisabled || loading) && (
                <CircularProgress size="16px" color="inherit" />
              )
            }
            onClick={handleRemove}
          >
            {translateUi("forms.confirm")}
          </Button>
          {errorMessage && (
            <Typography color="error" variant="caption">
              {errorMessage}
            </Typography>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
